import { Link } from "react-router-dom";
import SEOHead from "./SEOHead";
import { ArrowRight } from "lucide-react";

/**
 * Editorial layout used by static brief pages (events, learn, strains,
 * privacy, terms, accessibility). Hero + slot for body sections + an
 * optional sticky right-rail TOC list passed by the consumer.
 */
export default function StaticPage({
    title,
    eyebrow,
    h1,
    intro,
    cta,
    canonical,
    description,
    toc,
    children,
}) {
    return (
        <>
            <SEOHead title={title} description={description} canonical={canonical} />

            {/* Hero */}
            <section className="bg-zen-surface border-b border-zen-ink/10">
                <div className="max-w-[1100px] mx-auto px-6 sm:px-10 py-12 sm:py-16">
                    {eyebrow && (
                        <p className="text-[11px] uppercase tracking-[0.3em] text-zen-olive mb-4">
                            {eyebrow}
                        </p>
                    )}
                    <h1 className="font-serif font-light text-4xl sm:text-5xl lg:text-6xl text-zen-ink leading-[1.05] tracking-tight mb-6">
                        {h1}
                    </h1>
                    {intro && (
                        <p className="text-base sm:text-lg text-zen-ink-soft max-w-3xl leading-relaxed">
                            {intro}
                        </p>
                    )}
                    {cta && (
                        <div className="mt-7 flex flex-wrap gap-3">
                            {cta.map((c) => (
                                <Link
                                    key={c.label}
                                    to={c.href}
                                    className={`px-6 py-3 text-[11px] uppercase tracking-[0.22em] font-medium transition-colors inline-flex items-center gap-2 ${
                                        c.primary
                                            ? "bg-zen-ink text-zen-cream hover:bg-zen-olive-dark"
                                            : "border border-zen-ink text-zen-ink hover:bg-zen-ink hover:text-zen-cream"
                                    }`}
                                >
                                    {c.label} {c.primary && <ArrowRight size={13} />}
                                </Link>
                            ))}
                        </div>
                    )}
                </div>
            </section>

            {/* Body — optional TOC sidebar */}
            <section className="bg-zen-bg">
                <div className="max-w-[1100px] mx-auto px-6 sm:px-10 py-12 grid grid-cols-1 lg:grid-cols-12 gap-10">
                    {toc && toc.length > 0 && (
                        <aside className="lg:col-span-3">
                            <div className="lg:sticky lg:top-24">
                                <p className="text-[11px] uppercase tracking-[0.25em] text-zen-olive mb-4">
                                    On this page
                                </p>
                                <nav className="flex flex-col gap-2 text-sm">
                                    {toc.map((item) => (
                                        <a
                                            key={item.id}
                                            href={`#${item.id}`}
                                            className="text-zen-ink-soft hover:text-zen-ink border-l-2 border-zen-line hover:border-zen-olive pl-3 transition-colors py-0.5"
                                        >
                                            {item.label}
                                        </a>
                                    ))}
                                </nav>
                            </div>
                        </aside>
                    )}
                    <div className={toc?.length ? "lg:col-span-9" : "lg:col-span-12"}>
                        <article className="prose-zen max-w-none text-zen-ink space-y-10 text-[15px] leading-relaxed">
                            {children}
                        </article>
                    </div>
                </div>
            </section>
        </>
    );
}

export function Section({ id, title, children }) {
    return (
        <section id={id} className="scroll-mt-24">
            <h2 className="font-serif text-2xl sm:text-3xl text-zen-ink font-light leading-tight mb-4 tracking-tight">
                {title}
            </h2>
            <div className="space-y-3 text-zen-ink-soft">{children}</div>
        </section>
    );
}
